import { useMemo, useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, Settings } from 'lucide-react';
import { Error, Loading, ErrorBoundary, SafeAreaHeader } from './components';
import { AdminDashboardSkeleton, MenuPageSkeleton } from './components/skeletons';
import { initTelegram, onDeactivated } from './lib/telegram';
import { getAdminConfig } from './services/api';
import { saveAppMode, loadAppMode, usePersistedState } from './hooks/usePersistedState';
import type { ChainLocation } from './services/api-public';
import { resolveBot } from './services/api-public';

// Auto-reload on chunk load failure (stale deployment)
function lazyWithRetry<T extends Record<string, unknown>>(
  factory: () => Promise<T>,
): Promise<T> {
  return factory().catch((error: unknown) => {
    // Only reload once to avoid infinite loops
    const hasReloaded = sessionStorage.getItem('chunk_reload');
    if (!hasReloaded) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
    }
    throw error;
  });
}

// Lazy load pages for better initial load performance
const MenuPage = lazy(() => lazyWithRetry(() => import('./pages/MenuPage').then(m => ({ default: m.MenuPage }))));
const AdminPage = lazy(() => lazyWithRetry(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage }))));
const LocationPickerPage = lazy(() => lazyWithRetry(() => import('./pages/LocationPickerPage').then(m => ({ default: m.LocationPickerPage }))));

type AppMode = 'loading' | 'resolving' | 'admin' | 'client';
type UserRole = 'owner' | 'admin' | 'viewer' | 'driver' | null;

/** Timeout (ms) for bot resolution — prevents infinite "Conectando…" spinner. */
const RESOLVE_TIMEOUT_MS = 10_000;

// Clear reload flag on successful app load
sessionStorage.removeItem('chunk_reload');

function App() {
  const [mode, setMode] = useState<AppMode>('loading');
  const [_isOwner, setIsOwner] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Get restaurant ID and chain ID from URL params
  const { restaurantId: urlRestaurantId, chainId: urlChainId } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      restaurantId: params.get('restaurantId'),
      chainId: params.get('chainId'),
    };
  }, []);

  // Resolved IDs from bot resolution (when opened via ?startapp without params)
  const [resolvedRestaurantId, setResolvedRestaurantId] = useState<string | null>(null);
  const [resolvedChainId, setResolvedChainId] = useState<string | null>(null);

  // Effective chain ID: from URL or from bot resolution
  const chainId = urlChainId ?? resolvedChainId;

  // For chains: persist selected location
  const [selectedLocationId, setSelectedLocationId] = usePersistedState<string | null>(
    'selected_location',
    null,
    chainId ?? undefined
  );

  // Effective restaurantId: from URL, resolved, or from selected chain location
  const restaurantId = urlRestaurantId ?? resolvedRestaurantId ?? selectedLocationId;

  // Get initial mode from URL params
  const initialMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode'); // 'admin' or null
  }, []);

  // Effect 1: Bot resolution — runs ONLY when no restaurantId/chainId are present in the URL.
  // For standalone restaurants, also checks admin access in the same flow (avoiding a second
  // render cycle + API round-trip). For chains, sets resolvedChainId which shows the location picker.
  useEffect(() => {
    if (urlRestaurantId || urlChainId) return; // Already have IDs from URL

    setMode('resolving');

    // Prefetch MenuPage chunk while we wait for API response
    import('./pages/MenuPage').catch(() => {});

    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        setResolveError('No se pudo conectar con el servidor. Intenta de nuevo más tarde.');
        setMode('client');
      }
    }, RESOLVE_TIMEOUT_MS);

    resolveBot()
      .then(async (result) => {
        if (cancelled) return;
        clearTimeout(timeout);

        if (result.type === 'chain') {
          setResolvedChainId(result.id);
          setMode('loading');
          return;
        }

        // Standalone restaurant: resolve + check access in one flow
        const resolvedId = result.id;
        setResolvedRestaurantId(resolvedId);
        initTelegram();

        try {
          const config = await getAdminConfig(resolvedId);
          if (cancelled) return;
          const role = (config.userRole ?? 'owner') as UserRole;
          setUserRole(role);
          setIsOwner(role === 'owner');

          // Prefetch AdminPage chunk so admin↔client switch is instant
          if (role) import('./pages/AdminPage').catch(() => {});

          if (role === 'driver') {
            setMode('admin');
          } else if (initialMode === 'admin') {
            setMode('admin');
          } else {
            const persistedMode = loadAppMode(resolvedId);
            if (persistedMode) {
              setMode(persistedMode);
            } else {
              setMode(role === 'viewer' ? 'client' : 'admin');
            }
          }
        } catch {
          if (cancelled) return;
          setIsOwner(false);
          setUserRole(null);
          setMode('client');
        }
      })
      .catch(() => {
        if (cancelled) return;
        cancelled = true;
        clearTimeout(timeout);
        setResolveError('No se encontró un local asociado a este bot. Por favor, accede a través del link del local.');
        setMode('client');
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [urlRestaurantId, urlChainId, initialMode]);

  // Effect 2: Access check — runs when `restaurantId` changes from URL params or chain
  // location selection. Skipped when bot resolution (Effect 1) already handled access.
  useEffect(() => {
    // Skip if bot resolution already handled this restaurantId
    if (restaurantId && restaurantId === resolvedRestaurantId) return;

    if (!restaurantId) {
      // Don't override 'resolving' mode — bot resolution (Effect 1) is still in progress
      if (!urlRestaurantId && !urlChainId) return;
      setMode('client');
      return;
    }

    initTelegram();

    async function checkAccess() {
      try {
        // Try to fetch admin config - will fail with 403 if no access
        const config = await getAdminConfig(restaurantId!);
        const role = (config.userRole ?? 'owner') as UserRole;
        setUserRole(role);
        setIsOwner(role === 'owner');

        // Prefetch AdminPage chunk so admin↔client switch is instant
        if (role) import('./pages/AdminPage').catch(() => {});

        // Drivers always go to admin mode (they see DriverView)
        if (role === 'driver') {
          setMode('admin');
          return;
        }

        // If explicitly requested admin mode via URL, use that
        if (initialMode === 'admin') {
          setMode('admin');
          return;
        }

        // Otherwise, try to restore persisted mode (within 1 hour)
        const persistedMode = loadAppMode(restaurantId!);
        if (persistedMode) {
          setMode(persistedMode);
        } else {
          // Default to admin for owners/admins, client for viewers
          setMode(role === 'viewer' ? 'client' : 'admin');
        }
      } catch {
        // No access - show client view
        setIsOwner(false);
        setUserRole(null);
        setMode('client');
      }
    }

    checkAccess();
  }, [restaurantId, initialMode, urlRestaurantId, urlChainId, resolvedRestaurantId]);

  // Persist mode when it changes
  useEffect(() => {
    if (mode !== 'loading' && mode !== 'resolving' && restaurantId) {
      saveAppMode(mode, restaurantId);
    }
  }, [mode, restaurantId]);

  // Save mode when app is deactivated
  useEffect(() => {
    if (!restaurantId) return;
    const cleanup = onDeactivated(() => {
      if (mode !== 'loading' && mode !== 'resolving') {
        saveAppMode(mode, restaurantId);
      }
    });
    return cleanup;
  }, [mode, restaurantId]);

  // Trigger for pill click — incremented each time the header pill is tapped.
  // When in admin mode, switches to client mode first. The counter is passed to
  // MenuPage so it can reset its view to 'menu' and scroll to top.
  const [scrollToTopTrigger, setScrollToTopTrigger] = useState(0);

  // Toast state for mode switch
  const [modeToast, setModeToast] = useState<{ message: string; icon: 'eye' | 'settings'; closing: boolean } | null>(null);
  const modeToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showModeToast = useCallback((message: string, icon: 'eye' | 'settings') => {
    if (modeToastTimerRef.current) clearTimeout(modeToastTimerRef.current);
    setModeToast({ message, icon, closing: false });
    modeToastTimerRef.current = setTimeout(() => {
      setModeToast(prev => prev ? { ...prev, closing: true } : null);
      setTimeout(() => setModeToast(null), 150);
    }, 2000);
  }, []);
  useEffect(() => () => { if (modeToastTimerRef.current) clearTimeout(modeToastTimerRef.current); }, []);

  // Handle switching from admin to client view - refresh restaurant data
  // Must be before any conditional returns to follow React hooks rules
  const handlePreviewClick = useCallback(() => {
    if (!restaurantId) return;
    // Invalidate restaurant cache so MenuPage gets fresh data
    queryClient.invalidateQueries({ queryKey: ['restaurant', restaurantId] });
    // Reset scroll before switching so MenuPage mounts at top
    window.scrollTo(0, 0);
    // Increment trigger so MenuPage's scroll-to-top effect fires and
    // prevents persisted scroll restoration
    setScrollToTopTrigger(prev => prev + 1);
    setMode('client');
    showModeToast('Vista de cliente', 'eye');
  }, [queryClient, restaurantId, showModeToast]);

  // Handle selecting a location from the chain picker
  const handleSelectLocation = useCallback((location: ChainLocation) => {
    setSelectedLocationId(location.id);
  }, [setSelectedLocationId]);

  // Handle changing location (back to picker)
  const handleChangeLocation = useCallback(() => {
    setSelectedLocationId(null);
    setMode('loading');
  }, [setSelectedLocationId]);

  const handlePillClick = useCallback(() => {
    if (mode === 'admin') {
      if (!restaurantId) return;
      queryClient.invalidateQueries({ queryKey: ['restaurant', restaurantId] });
      // Reset scroll immediately before the mode switch so MenuPage mounts at top
      window.scrollTo(0, 0);
      setMode('client');
      showModeToast('Vista de cliente', 'eye');
    }
    setScrollToTopTrigger(prev => prev + 1);
    // If already in client/menu mode, the trigger change will cause MenuPage
    // to reset its view and scroll to top via useEffect.
    // For the admin→client transition, scrollToTopTrigger propagates on the
    // next render when MenuPage mounts.
  }, [mode, restaurantId, queryClient, showModeToast]);

  // Chain mode: show location picker if no location selected
  if (chainId && !restaurantId) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading text="Cargando locales..." />}>
          <LocationPickerPage
            chainId={chainId}
            onSelectLocation={handleSelectLocation}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (mode === 'resolving') {
    return <Loading text="Conectando con el local..." />;
  }

  if (!restaurantId && !chainId) {
    return (
      <Error
        message={resolveError ?? "No se especificó un local. Por favor, accede a través del link del local."}
      />
    );
  }

  if (mode === 'loading') {
    return <Loading />;
  }

  if (!restaurantId) {
    return <Loading />;
  }

  // Admin mode - owners, admins, viewers, and drivers
  const hasAdminAccess = userRole !== null;
  // Mode switch toast (shared between both branches)
  const ModeIcon = modeToast?.icon === 'eye' ? Eye : Settings;
  const modeToastElement = modeToast && (
    <div className={`fixed inset-x-0 z-50 flex justify-center pointer-events-none ${modeToast.closing ? 'animate-toast-out-top' : 'animate-toast-in-top'}`} style={{ top: 'calc(var(--safe-area-top, 0px) + 12px)' }}>
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/75 backdrop-blur-md shadow-2xl pointer-events-auto">
        <ModeIcon size={16} className="text-blue-400 shrink-0" />
        <span className="text-white/90 text-[13px] font-medium">{modeToast.message}</span>
      </div>
    </div>
  );

  if (mode === 'admin' && hasAdminAccess) {
    return (
      <>
        <SafeAreaHeader restaurantId={restaurantId} onPillClick={handlePillClick} />
        <ErrorBoundary>
          <Suspense fallback={<AdminDashboardSkeleton />}>
            <AdminPage
              restaurantId={restaurantId}
              onPreviewClick={handlePreviewClick}
            />
          </Suspense>
        </ErrorBoundary>
        {modeToastElement}
      </>
    );
  }

  // Client/preview mode
  // Allow switching to admin for owners and admins (not viewers/drivers from client mode)
  const canSwitchToAdmin = userRole === 'owner' || userRole === 'admin';
  return (
    <>
      <SafeAreaHeader restaurantId={restaurantId} onPillClick={handlePillClick} />
      <ErrorBoundary>
        <Suspense fallback={<MenuPageSkeleton />}>
          <MenuPage
            restaurantId={restaurantId}
            onAdminClick={canSwitchToAdmin ? () => { setMode('admin'); showModeToast('Panel admin', 'settings'); } : undefined}
            onChangeLocation={chainId ? handleChangeLocation : undefined}
            scrollToTopTrigger={scrollToTopTrigger}
          />
        </Suspense>
      </ErrorBoundary>
      {modeToastElement}
    </>
  );
}

export default App;

